import {Injectable} from '@angular/core';
import {Params} from '@angular/router';
import {BookingV2StateService} from './booking-v2-state.service';
import {QueryParams} from '../../../types/QueryParams';
import {ToolsService} from '../../../services/tools.service';
import {RoutesQueryParamsEnum} from '../../../enums/RoutesEnums';
import * as dayjs from 'dayjs';
import 'dayjs/locale/sv';
import * as isoWeek from 'dayjs/plugin/isoWeek';
import {BookingParamsEnum} from '../enums/booking-enums';
import {BookingState} from '../types/booking-state';

@Injectable({
  providedIn: 'any',
})
export class BookingV2RouteResolverService {
  private params: Params = {};
  private queryParams: QueryParams = {};
  private currentState: BookingState;
  private newState: BookingState;
  private initialRun: boolean = true;
  private readonly urlPathProcessorsMap: Array<Function> = [];

  constructor(
    private tools: ToolsService,
    private bookingState: BookingV2StateService,
  ) {
    dayjs.locale('sv');
    dayjs.extend(isoWeek);
    this.createUrlPathProcessorsMap();
    this.initialRun = true;
  }

  public onRouteChange(params: Params, queryParams: QueryParams, isRedirected: boolean): void {
    this.params = params;
    this.queryParams = queryParams;
    this.resolveParams();
    this.bookingState.isCurrentRouteFromRedirect = isRedirected === true;
    this.initialRun = false;
  }

  private resolveParams(): void {
    this.currentState = this.tools.clone(this.bookingState.state$.getValue());
    this.newState = this.tools.clone(this.bookingState.getDefaultState());
    Object.keys(this.params).forEach((key) => {
      if (!this.tools.isNil(this.urlPathProcessorsMap[key])) {
        this.urlPathProcessorsMap[key](this.params[key]);
      }
    });
    Object.keys(this.queryParams).forEach((key) => {
      if (!this.tools.isNil(this.urlPathProcessorsMap[key])) {
        this.urlPathProcessorsMap[key](this.queryParams[key]);
      }
    });
    if (!this.tools.isEqual(this.newState, this.currentState)) {
      this.bookingState.updateState(this.newState);
    }
  }

  private createUrlPathProcessorsMap() {
    this.urlPathProcessorsMap[BookingParamsEnum.CAREPROVIDER_ID] = (paramValue: string) =>
      this.updateProviderId(paramValue);
    this.urlPathProcessorsMap[BookingParamsEnum.SERVICE_ID] = (paramValue: string) => this.updateServiceId(paramValue);
    this.urlPathProcessorsMap[BookingParamsEnum.TIMESLOT] = (paramValue: string) => this.updateTimeslot(paramValue);
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.BOOKING_REFERRAL] = (paramValue: string) =>
      this.updateReferral(paramValue);
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.BOOKING_STEP] = (paramValue: string) => this.updateStep(paramValue);
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.BOOKING_CALENDAR_DATE] = (paramValue: string) =>
      this.updateCalendarDate(paramValue);
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.BOOKING_FORCE_CALENDAR_DATE] = (paramValue: string) =>
      this.updateForceCalendarDate(paramValue);
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.BOOKING_SELECTED_DATETIME] = (paramValue: string) =>
      this.updateSelectedDateTime(paramValue);
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.BOOKING_SELECTED_SERVICE] = (paramValue: string) =>
      this.updateSelectedService(paramValue);
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.BOOKING_SELECTED_STAFFMEMBER] = (paramValue: string) =>
      this.updateSelectedStaffMember(paramValue);
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.BOOKING_ALERT] = (paramValue: string) =>
      this.updateAlert(paramValue);
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.BOOKING_NUMBER_OF_TIMESLOTS] = (paramValue: string) =>
      this.updateNumberOfTimeslots(paramValue);
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.BOOKING_SERVICE_GROUP_ID] = (paramValue: string) =>
      this.updateSelectedGroup(paramValue);
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.BOOKING_TIMESLOT_MATCH] = (paramValue: string) =>
      this.updateTimeslotMatch(paramValue);
    this.urlPathProcessorsMap[RoutesQueryParamsEnum.BOOKING_TWO_FACTOR_AUTH] = (paramValue: string) =>
      this.updateTwoFactorAuth(paramValue);
  }

  private updateStep(step: string): void {
    let stepNumber: number = parseInt(step, 10);
    if (stepNumber === 0 || stepNumber > 4) {
      stepNumber = 1;
    }
    this.newState.step = stepNumber;
  }

  private updateCalendarDate(date: string): void {
    const startDate: string = dayjs(date, 'YYYYMMDD').startOf('week').format('YYYY-MM-DD');
    const endDate: string = dayjs(date, 'YYYYMMDD').endOf('week').format('YYYY-MM-DD');
    this.newState.calendarWeekStartDate = startDate;
    this.newState.calendarWeekEndDate = endDate;
  }

  private updateForceCalendarDate(force: string): void {
    this.newState.forceCalendarDate = force === '1';
  }

  private updateSelectedDateTime(dateTime: string): void {
    this.newState.selectedDateTime = dateTime;
    const date: string = dayjs(dateTime, this.bookingState.selectedDateTimeStorageFormat).format('YYYYMMDD');
    this.updateCalendarDate(date);
  }

  private updateSelectedService(serviceId: string): void {
    this.newState.selectedBookingServiceId = serviceId;
  }

  private updateSelectedStaffMember(staffMemberId: string): void {
    this.newState.selectedStaffMemberId = staffMemberId;
  }

  private updateProviderId(providerId: string): void {
    this.newState.providerId = parseInt(providerId, 10);
  }

  private updateServiceId(serviceId: string): void {
    this.newState.serviceId = parseInt(serviceId, 10);
  }

  private updateTimeslot(timeslot: string): void {
    this.newState.timeslot = parseInt(timeslot, 10).toString();
  }

  private updateReferral(referral: string): void {
    this.newState.referral = decodeURI(decodeURI(referral));
  }

  private updateAlert(alert: string): void {
    this.newState.alert = parseInt(alert, 10);
  }

  private updateNumberOfTimeslots(nots: string): void {
    this.newState.numberOfTimeslots = parseInt(nots, 10);
  }

  private updateSelectedGroup(group: string) {
    this.newState.serviceGroup = parseInt(group, 10);
  }

  private updateTimeslotMatch(paramValue: string) {
    this.newState.TimeslotMatchExisted = paramValue === '1';
  }

  private updateTwoFactorAuth(paramValue: string) {
    this.newState.twoFactorAuthenticationRequired = paramValue === '1';
  }
}
